import React, { useEffect, useMemo, useState } from "react";
import {
    Accordion,
    Badge,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
} from "@mantine/core";

import type {
    FoodOrderDto,
    Language,
} from "../types";
import {
    IconToolsKitchen2,
} from "@tabler/icons-react";

type Props = {
    apiUrl?: string;
    eventId: number;
    personId: number;
    language: Language;
    t: any;
};

type FoodOrderStatus =
    | "Received"
    | "InProgress"
    | "Ready"
    | "Delivered"
    | "Cancelled";


function normalizeStatus(status: FoodOrderDto["status"]): FoodOrderStatus {
    if (
        status === "Received" ||
        status === "InProgress" ||
        status === "Ready" ||
        status === "Delivered" ||
        status === "Cancelled"
    ) {
        return status;
    }

    switch (Number(status)) {
        case 0: return "Received";
        case 1: return "InProgress";
        case 2: return "Ready";
        case 3: return "Delivered";
        case 4: return "Cancelled";
        default: return "Received";
    }
}

const MICROWAVE_COUNT = 4;
const MINUTES_PER_BOX = 7;

function getRemainingBoxCount(order: FoodOrderDto): number {
    if (
        order.splitOrder &&
        (order.units?.length ?? 0) > 0
    ) {
        return (order.units ?? []).filter(unit => {
            const status = normalizeStatus(unit.status);

            return (
                status !== "Ready" &&
                status !== "Delivered" &&
                status !== "Cancelled"
            );
        }).length;
    }

    return order.lines.reduce(
        (sum, line) =>
            sum + Math.max(0, Number(line.quantity) || 0),
        0
    );
}

function isActiveKitchenOrder(order: FoodOrderDto): boolean {
    const status = normalizeStatus(order.status);

    if (
        status === "Ready" ||
        status === "Delivered" ||
        status === "Cancelled"
    ) {
        return false;
    }

    if (
        status === "Received" &&
        order.scheduledFor
    ) {
        const scheduled =
            new Date(order.scheduledFor).getTime();

        if (
            Number.isFinite(scheduled) &&
            scheduled > Date.now()
        ) {
            return false;
        }
    }

    return (
        status === "Received" ||
        status === "InProgress"
    );
}

function getQueuePriority(order: FoodOrderDto): number {
    const status = normalizeStatus(order.status);

    if (status === "InProgress") return 1;
    if (status === "Received") return 2;

    return 99;
}

function estimateOrderMinutes(
    targetOrder: FoodOrderDto,
    kitchenOrders: FoodOrderDto[]
): number {
    const targetStatus = normalizeStatus(targetOrder.status);

    if (
        targetStatus === "Ready" ||
        targetStatus === "Delivered" ||
        targetStatus === "Cancelled"
    ) {
        return 0;
    }

    if (isFuturePreOrder(targetOrder)) {
        return 0;
    }

    const activeOrders = [...kitchenOrders]
        .filter(isActiveKitchenOrder)
        .sort((a, b) => {
            const pa = getQueuePriority(a);
            const pb = getQueuePriority(b);

            if (pa !== pb) return pa - pb;

            return (
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            );
        });

    let boxesAhead = 0;

    for (const order of activeOrders) {
        if (order.id === targetOrder.id) {
            break;
        }

        boxesAhead += getRemainingBoxCount(order);
    }

    const targetBoxes = getRemainingBoxCount(targetOrder);

    if (targetBoxes <= 0) {
        return 0;
    }

    return (
        Math.ceil(
            (boxesAhead + targetBoxes) /
            MICROWAVE_COUNT
        ) * MINUTES_PER_BOX
    );
}

function getEstimatedReadyAt(minutes: number): Date {
    return new Date(
        Date.now() + minutes * 60 * 1000
    );
}

function formatEstimatedTime(
    date: Date,
    language: Language
): string {
    return date.toLocaleTimeString(
        language === "da" ? "da-DK" : "en-GB",
        {
            hour: "2-digit",
            minute: "2-digit",
        }
    );
}

function isFuturePreOrder(order: FoodOrderDto) {
    if (!order.scheduledFor) return false;

    const time = new Date(order.scheduledFor).getTime();

    return (
        Number.isFinite(time) &&
        time > Date.now() &&
        normalizeStatus(order.status) === "Received"
    );
}

function getStatusText(order: FoodOrderDto, language: Language) {
    if (isFuturePreOrder(order)) {
        return language === "da" ? "Forudbestilt" : "Pre-order";
    }

    const status = normalizeStatus(order.status);

    if (language === "da") {
        switch (status) {
            case "Received": return "Ny ordre";
            case "InProgress": return "I gang";
            case "Ready": return "Klar til afhentning";
            case "Delivered": return "Udleveret";
            case "Cancelled": return "Annulleret";
        }
    }

    switch (status) {
        case "Received": return "New order";
        case "InProgress": return "In progress";
        case "Ready": return "Ready for pickup";
        case "Delivered": return "Delivered";
        case "Cancelled": return "Cancelled";
    }
}

function getStatusColor(order: FoodOrderDto) {
    if (isFuturePreOrder(order)) return "violet";

    switch (normalizeStatus(order.status)) {
        case "Received": return "red";
        case "InProgress": return "orange";
        case "Ready": return "green";
        case "Delivered": return "gray";
        case "Cancelled": return "dark";
        default: return "gray";
    }
}

function formatDateTime(value: string, language: Language) {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString(
        language === "da" ? "da-DK" : "en-GB",
        {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }
    );
}


function getUnitStatusText(
    status: FoodOrderStatus,
    language: Language
) {
    if (language === "da") {
        switch (status) {
            case "Received": return "Ny";
            case "InProgress": return "I gang";
            case "Ready": return "Klar";
            case "Delivered": return "Udleveret";
            case "Cancelled": return "Annulleret";
        }
    }

    switch (status) {
        case "Received": return "New";
        case "InProgress": return "In progress";
        case "Ready": return "Ready";
        case "Delivered": return "Delivered";
        case "Cancelled": return "Cancelled";
    }
}

function getUnitStatusColor(
    status: FoodOrderStatus
) {
    switch (status) {
        case "Received": return "red";
        case "InProgress": return "orange";
        case "Ready": return "green";
        case "Delivered": return "gray";
        case "Cancelled": return "dark";
        default: return "gray";
    }
}

export default function FoodOrdersSection({
    apiUrl,
    eventId,
    personId,
    language,
    t,
}: Props) {
    const [orders, setOrders] = useState<FoodOrderDto[]>([]);
    const [kitchenOrders, setKitchenOrders] =
        useState<FoodOrderDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!apiUrl || !eventId || !personId) return;

        let mounted = true;

        const load = async () => {
            try {
                const [
                    personOrdersRes,
                    kitchenOrdersRes,
                ] = await Promise.all([
                    fetch(
                        `${apiUrl}/api/events/${eventId}/food/persons/${personId}/orders`
                    ),
                    fetch(
                        `${apiUrl}/api/events/${eventId}/food/orders`
                    ),
                ]);

                if (!personOrdersRes.ok) {
                    const message =
                        await personOrdersRes.text();

                    throw new Error(
                        message ||
                        `HTTP ${personOrdersRes.status}`
                    );
                }

                if (!kitchenOrdersRes.ok) {
                    const message =
                        await kitchenOrdersRes.text();

                    throw new Error(
                        message ||
                        `HTTP ${kitchenOrdersRes.status}`
                    );
                }

                const [
                    personOrdersData,
                    kitchenOrdersData,
                ] = await Promise.all([
                    personOrdersRes.json(),
                    kitchenOrdersRes.json(),
                ]);

                if (!mounted) return;

                setOrders(
                    Array.isArray(personOrdersData)
                        ? personOrdersData
                        : []
                );

                setKitchenOrders(
                    Array.isArray(kitchenOrdersData)
                        ? kitchenOrdersData
                        : []
                );

                setError(null);
            } catch (err) {
                if (!mounted) return;

                setError(
                    err instanceof Error
                        ? err.message
                        : t.foodOrdersError
                );
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        const timer = window.setInterval(load, 5000);

        return () => {
            mounted = false;
            window.clearInterval(timer);
        };
    }, [apiUrl, eventId, personId, t.foodOrdersError]);

    const visibleOrders = useMemo(() => {
        return [...orders]
            .filter(order => {
                const status =
                    normalizeStatus(order.status);

                return (
                    status !== "Delivered" &&
                    status !== "Cancelled"
                );
            })
            .sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
            );
    }, [orders]);

    const activeCount = visibleOrders.length;

    if (loading) {
        return null;
    }

    if (error) {
        return null;
    }

    if (visibleOrders.length === 0) {
        return null;
    }

    return (
        <Accordion.Item value="food-orders">
            <Accordion.Control>
                <Group
                    justify="space-between"
                    wrap="nowrap"
                    w="100%"
                >
                    <Group
                        gap="sm"
                        wrap="nowrap"
                    >
                        <div
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fff0f1",
                                color: "#fa5252",
                                flexShrink: 0,
                            }}
                        >
                            <IconToolsKitchen2 size={16} />
                        </div>

                        <Text>
                            {t.foodOrders}
                        </Text>
                    </Group>

                    <Badge
                        color="red"
                        variant="filled"
                        circle
                    >
                        {visibleOrders.length}
                    </Badge>
                </Group>
            </Accordion.Control>

            <Accordion.Panel>
                {loading ? (
                    <Group>
                        <Loader color="red" size="sm" />
                        <Text size="sm">{t.foodOrdersLoading}</Text>
                    </Group>
                ) : error ? (
                    <Text size="sm" c="red">
                        {error}
                    </Text>
                ) : visibleOrders.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t.noFoodOrders}
                    </Text>
                ) : (
                    <Stack gap="sm">
                        {visibleOrders.map(order => {
                            const status =
                                normalizeStatus(order.status);

                            const estimatedMinutes =
                                estimateOrderMinutes(
                                    order,
                                    kitchenOrders
                                );

                            const estimatedReadyAt =
                                getEstimatedReadyAt(
                                    estimatedMinutes
                                );

                            return (
                                <Paper
                                    key={order.id}
                                    withBorder
                                    radius="md"
                                    p="sm"
                                >
                                    <Stack gap={7}>
                                        <Group justify="space-between" align="flex-start">
                                            <div>
                                                <Text fw={700}>
                                                    {t.foodOrder} #{order.id}
                                                </Text>

                                                <Text size="xs" c="dimmed">
                                                    {formatDateTime(
                                                        order.createdAt,
                                                        language
                                                    )}
                                                </Text>
                                            </div>

                                            <Badge
                                                color={getStatusColor(order)}
                                                variant="light"
                                            >
                                                {getStatusText(order, language)}
                                            </Badge>
                                        </Group>

                                        {order.scheduledFor &&
                                            isFuturePreOrder(order) && (
                                                <Text size="sm" fw={600}>
                                                    {t.foodScheduledFor}:{" "}
                                                    {formatDateTime(
                                                        order.scheduledFor,
                                                        language
                                                    )}
                                                </Text>
                                            )}

                                        {status !== "Ready" &&
                                            !isFuturePreOrder(order) &&
                                            estimatedMinutes > 0 && (
                                                <Paper
                                                    radius="md"
                                                    p="xs"
                                                    bg="blue.0"
                                                >
                                                    <Text
                                                        size="sm"
                                                        fw={700}
                                                        c="blue.8"
                                                    >
                                                        ⏱{" "}
                                                        {language === "da"
                                                            ? `Estimeret klar om ca. ${estimatedMinutes} min.`
                                                            : `Estimated ready in about ${estimatedMinutes} min.`}
                                                    </Text>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                        mt={2}
                                                    >
                                                        {language === "da"
                                                            ? "Ca. kl."
                                                            : "Around"}{" "}
                                                        {formatEstimatedTime(
                                                            estimatedReadyAt,
                                                            language
                                                        )}
                                                      
                                                    </Text>
                                                </Paper>
                                            )}

                                        <Stack gap={2}>
                                            {order.lines.map((line, index) => (
                                                <Text
                                                    key={`${line.menuItemId}-${index}`}
                                                    size="sm"
                                                >
                                                    {line.quantity > 1
                                                        ? `${line.quantity} × `
                                                        : ""}
                                                    {line.name}
                                                </Text>
                                            ))}
                                        </Stack>

                                        {order.splitOrder &&
                                            (order.units?.length ?? 0) > 0 && (() => {
                                                const units =
                                                    order.units ?? [];

                                                const readyCount =
                                                    units.filter(
                                                        unit =>
                                                            normalizeStatus(
                                                                unit.status
                                                            ) === "Ready"
                                                    ).length;

                                                const deliveredCount =
                                                    units.filter(
                                                        unit =>
                                                            normalizeStatus(
                                                                unit.status
                                                            ) === "Delivered"
                                                    ).length;

                                                const completedCount =
                                                    readyCount +
                                                    deliveredCount;

                                                return (
                                                    <Paper
                                                        withBorder
                                                        radius="md"
                                                        p="xs"
                                                    >
                                                        <Stack gap="xs">
                                                            <Group
                                                                justify="space-between"
                                                                wrap="nowrap"
                                                            >
                                                                <Text
                                                                    size="sm"
                                                                    fw={700}
                                                                >
                                                                    🏷️ {completedCount}/{units.length}{" "}
                                                                    {language === "da"
                                                                        ? "klar"
                                                                        : "ready"}
                                                                </Text>

                                                                {readyCount > 0 && (
                                                                    <Badge
                                                                        color="green"
                                                                        variant="light"
                                                                    >
                                                                        {readyCount}{" "}
                                                                        {language === "da"
                                                                            ? "klar nu"
                                                                            : "ready now"}
                                                                    </Badge>
                                                                )}
                                                            </Group>

                                                            {units
                                                                .slice()
                                                                .sort(
                                                                    (a, b) =>
                                                                        a.unitNumber -
                                                                        b.unitNumber
                                                                )
                                                                .map(unit => {
                                                                    const unitStatus =
                                                                        normalizeStatus(
                                                                            unit.status
                                                                        );

                                                                    return (
                                                                        <Group
                                                                            key={unit.id}
                                                                            justify="space-between"
                                                                            wrap="nowrap"
                                                                            p={6}
                                                                            style={{
                                                                                borderRadius: 8,
                                                                                background:
                                                                                    "var(--mantine-color-gray-0)",
                                                                            }}
                                                                        >
                                                                            <div>
                                                                                <Text
                                                                                    size="sm"
                                                                                    fw={600}
                                                                                >
                                                                                    {unit.unitNumber}/{unit.unitCount}
                                                                                    {" · "}
                                                                                    {unit.name}
                                                                                </Text>
                                                                            </div>

                                                                            <Badge
                                                                                color={
                                                                                    getUnitStatusColor(
                                                                                        unitStatus
                                                                                    )
                                                                                }
                                                                                variant="light"
                                                                            >
                                                                                {getUnitStatusText(
                                                                                    unitStatus,
                                                                                    language
                                                                                )}
                                                                            </Badge>
                                                                        </Group>
                                                                    );
                                                                })}
                                                        </Stack>
                                                    </Paper>
                                                );
                                            })()}

                                        {order.note && (
                                            <Text size="sm" c="dimmed">
                                                {t.note}: {order.note}
                                            </Text>
                                        )}

                                        {(
                                            normalizeStatus(order.status) === "Ready" ||
                                            (
                                                order.splitOrder &&
                                                (order.units ?? []).some(
                                                    unit =>
                                                        normalizeStatus(
                                                            unit.status
                                                        ) === "Ready"
                                                )
                                            )
                                        ) && (
                                                <Paper
                                                    radius="md"
                                                    p="xs"
                                                    bg="green.0"
                                                >
                                                    <Text size="sm" fw={700} c="green.8">
                                                        {normalizeStatus(order.status) === "Ready"
                                                            ? t.foodReadyMessage
                                                            : language === "da"
                                                                ? "En del af din ordre er klar til afhentning."
                                                                : "Part of your order is ready for pickup."}
                                                    </Text>
                                                </Paper>
                                            )}
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Accordion.Panel>
        </Accordion.Item>
    );
}